import { Pencil, RotateCcw, Search, Trash2, X } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { procedureService } from '../api/services/procedure.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { ListPagination } from '../components/ui/list-pagination'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import type { Procedure, ProcedureFormPayload } from '../types/procedure'

type ActiveFilter = 'all' | 'active' | 'disabled'
const ACTIVE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Inactive' },
]

const procedureSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().max(5000).optional(),
  is_active: z.boolean(),
})

type ProcedureFormValues = z.infer<typeof procedureSchema>

const emptyForm: ProcedureFormValues = {
  name: '',
  description: '',
  is_active: true,
}

export function ProceduresPage() {
  const { can } = useAuth()
  const [procedures, setProcedures] = useState<Procedure[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [withTrashed, setWithTrashed] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Procedure | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Procedure | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)

  const form = useForm<ProcedureFormValues>({
    resolver: zodResolver(procedureSchema),
    defaultValues: emptyForm,
  })

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (search.trim()) count += 1
    if (activeFilter !== 'all') count += 1
    if (withTrashed) count += 1
    return count
  }, [activeFilter, search, withTrashed])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setActiveFilter('all')
    setWithTrashed(false)
  }

  const loadProcedures = useCallback(async () => {
    try {
      const response = await procedureService.list({
        page,
        per_page: perPage,
        only_trashed: withTrashed || undefined,
        search: search.trim() || undefined,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setProcedures(items)
      setMeta(nextMeta)
    } catch {
      setProcedures([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [activeFilter, page, perPage, search, withTrashed])

  useEffect(() => {
    void loadProcedures()
  }, [loadProcedures])

  useEffect(() => {
    setPage(1)
  }, [activeFilter, search, withTrashed])

  const openCreate = () => {
    setEditing(null)
    form.reset(emptyForm)
    setFormOpen(true)
  }

  const openEdit = (procedure: Procedure) => {
    setEditing(procedure)
    form.reset({
      name: procedure.name,
      description: procedure.description ?? '',
      is_active: procedure.is_active,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
    form.reset(emptyForm)
  }

  const onSubmit = async (values: ProcedureFormValues) => {
    setSubmitting(true)

    const payload: ProcedureFormPayload = {
      name: values.name,
      description: values.description?.trim() || null,
      is_active: values.is_active,
    }

    try {
      if (editing) {
        await procedureService.update(editing.id, payload)
      } else {
        await procedureService.create(payload)
      }

      closeForm()
      await loadProcedures()
    } catch {
      // handled globally
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return
    }

    setDeleting(true)

    try {
      await procedureService.remove(deleteTarget.id)
      setDeleteTarget(null)
      await loadProcedures()
    } catch {
      // handled globally
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (procedure: Procedure) => {
    try {
      await procedureService.restore(procedure.id)
      await loadProcedures()
    } catch {
      // handled globally
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Procedures</h1>
            <p className="mt-1 text-sm text-ink-600">
              Manage procedure reference data for cases.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={withTrashed}
                onChange={(event) => setWithTrashed(event.target.checked)}
              />
              Show deleted
            </label>

            <PermissionGate permission="procedures-create">
              <Button onClick={openCreate}>Add Procedure</Button>
            </PermissionGate>
          </div>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Name</FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  placeholder="Search procedure name…"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSearch(searchInput.trim())
                    }
                  }}
                />
              </div>
            </div>
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={ACTIVE_OPTIONS}
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as ActiveFilter)}
                placeholder="All"
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Description</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && procedures.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-sm text-ink-500">
                      No procedures found.
                    </td>
                  </tr>
                ) : (
                  procedures.map((procedure) => (
                    <tr key={procedure.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3">{procedure.name}</td>
                      <td className="max-w-xs truncate px-4 py-3 text-ink-600" title={procedure.description ?? undefined}>
                        {procedure.description?.trim() || '—'}
                      </td>
                      <td className="px-4 py-3">{procedure.is_active ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">
                        {procedure.deleted_at ? (
                          <span className="text-danger">Deleted</span>
                        ) : (
                          <span className="text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          {can('procedures-update') && !procedure.deleted_at && (
                            <IconActionButton
                              icon={Pencil}
                              label="Edit"
                              onClick={() => openEdit(procedure)}
                            />
                          )}
                          {can('procedures-delete') && !procedure.deleted_at && (
                            <IconActionButton
                              icon={Trash2}
                              label="Delete"
                              variant="destructive"
                              onClick={() => setDeleteTarget(procedure)}
                            />
                          )}
                          {can('procedures-restore') && procedure.deleted_at && (
                            <IconActionButton
                              icon={RotateCcw}
                              label="Restore"
                              onClick={() => void handleRestore(procedure)}
                            />
                          )}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n)
            setPage(1)
          }}
        />

        <SoftDeleteConfirmDialog
          open={deleteTarget !== null}
          entityLabel="procedure"
          itemName={deleteTarget?.name ?? ''}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={deleting}
        />

        {formOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">
                {editing ? 'Edit Procedure' : 'Create Procedure'}
              </h2>

              <form className="mt-6 space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormRequiredLegend />
                <div>
                  <FieldLabel required>Name</FieldLabel>
                  <Input {...form.register('name')} />
                  {form.formState.errors.name && (
                    <p className="mt-1 text-xs text-danger">{form.formState.errors.name.message}</p>
                  )}
                </div>

                <div>
                  <FieldLabel>Description</FieldLabel>
                  <textarea
                    className="min-h-24 w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
                    {...form.register('description')}
                  />
                  {form.formState.errors.description && (
                    <p className="mt-1 text-xs text-danger">{form.formState.errors.description.message}</p>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" {...form.register('is_active')} />
                  Active
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Procedure'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
